import { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  X, Clock, User, Table2, RotateCcw, Trash2, 
  ShoppingCart, ChevronRight, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function ParkedOrdersDrawer({ open, onClose, orders, onResume, onRefresh }) {
  const { api } = useAuth();
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const handleDelete = async () => {
    if (!deletingOrder) return;
    
    setDeleting(true);
    try {
      await api.delete(`/orders/${deletingOrder.id}/parked`);
      toast.success('Parked order deleted');
      setDeletingOrder(null);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete parked order:', error);
      toast.error('Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader className="border-b border-slate-200 pb-4">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Parked Orders
                {orders.length > 0 && (
                  <Badge variant="secondary">{orders.length}</Badge>
                )}
              </SheetTitle>
              <Button variant="ghost" size="icon" onClick={onRefresh}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="py-4 space-y-3 overflow-y-auto max-h-[calc(100vh-120px)]">
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No parked orders</p>
                <p className="text-sm text-slate-400">
                  Orders you park will appear here
                </p>
              </div>
            ) : (
              orders.map(order => (
                <div
                  key={order.id}
                  className="bg-slate-50 rounded-lg p-4 hover:bg-slate-100 transition-colors"
                  data-testid={`parked-order-${order.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        {order.customer_name && (
                          <span className="flex items-center gap-1 text-sm font-medium">
                            <User className="w-4 h-4 text-slate-400" />
                            {order.customer_name}
                          </span>
                        )}
                        {order.table_number && (
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <Table2 className="w-4 h-4" />
                            Table {order.table_number}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(order.parked_at || order.created_at)}
                      </div>
                    </div>
                    <span className="font-bold text-orange-500">
                      £{order.total?.toFixed(2) || '0.00'}
                    </span>
                  </div>

                  {/* Items Preview */}
                  <div className="space-y-1 mb-3">
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">
                          {item.quantity}x {item.name}
                        </span>
                        <span className="text-slate-500">
                          £{(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-xs text-slate-400">
                        +{order.items.length - 3} more items
                      </p>
                    )}
                  </div>

                  {/* Notes */}
                  {order.notes && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-3 text-sm text-yellow-800">
                      {order.notes}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeletingOrder(order)}
                      data-testid={`delete-parked-${order.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      onClick={() => onResume(order)}
                      data-testid={`resume-parked-${order.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Resume Order
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingOrder} onOpenChange={() => setDeletingOrder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Parked Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this parked order. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Order'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
